import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";

function Shell({ children }) {
  return (
    <div className="min-h-screen flex">
      <div className="hidden md:flex md:w-1/2 cork-board relative items-center justify-center p-12">
        <div className="paper-card bg-white p-10 rounded-xl rotate-1 max-w-sm relative">
          <div className="push-pin" style={{"--pin":"#C79B6B"}}/>
          <h2 className="text-4xl font-bold mb-3" style={{fontFamily:'Outfit'}}>Forgot it?</h2>
          <p className="handwritten text-2xl text-[#C79B6B]">It happens to all of us.</p>
        </div>
      </div>
      <div className="w-full md:w-1/2 flex items-center justify-center p-8 bg-[#F2F4F2]">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center mb-10" data-testid="reset-logo-link">
            <img src="/logo-full.png" alt="LIMYA" className="h-9 w-auto"/>
          </Link>
          {children}
        </div>
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/users/password-reset/request", { email }); }
    finally { setSent(true); setLoading(false); }
  };

  return (
    <Shell>
      <h1 className="text-3xl font-bold mb-2" style={{fontFamily:'Outfit'}}>Reset your password</h1>
      <p className="text-sm text-[#222]/60 mb-6">We'll email you a link to set a new one.</p>
      {sent ? (
        <div className="bg-[#2E6B57]/15 border border-[#2E6B57]/30 rounded-xl p-4 text-sm" data-testid="forgot-sent">
          If an account exists for that email, a reset link is on its way. Check your inbox.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#222]/60">Email</label>
            <input type="email" required value={email} onChange={e=>setEmail(e.target.value)}
              className="w-full mt-1 px-4 py-3 bg-white border border-black/10 rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57]"
              data-testid="forgot-email-input"/>
          </div>
          <button type="submit" disabled={loading} className="btn-lime w-full py-3.5 disabled:opacity-60" data-testid="forgot-submit-btn">
            {loading ? "Sending…" : "Send reset link"}
          </button>
        </form>
      )}
      <p className="mt-6 text-sm text-[#222]/60 text-center">
        <Link to="/login" className="text-[#245544] font-semibold">← Back to sign in</Link>
      </p>
    </Shell>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/users/password-reset/confirm", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Reset failed");
    }
    setLoading(false);
  };

  return (
    <Shell>
      <h1 className="text-3xl font-bold mb-2" style={{fontFamily:'Outfit'}}>Set a new password</h1>
      <p className="text-sm text-[#222]/60 mb-6">Pick something memorable but hard to guess.</p>
      {done ? (
        <div className="bg-[#2E6B57]/15 border border-[#2E6B57]/30 rounded-xl p-4 text-sm" data-testid="reset-done">
          Password updated. Redirecting to sign-in…
        </div>
      ) : !token ? (
        <p className="text-sm text-red-600" data-testid="reset-missing-token">This link is missing its token. Use the link from your email.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#222]/60">New password</label>
            <input type="password" required minLength={6} value={password} onChange={e=>setPassword(e.target.value)}
              className="w-full mt-1 px-4 py-3 bg-white border border-black/10 rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57]"
              data-testid="reset-password-input"/>
          </div>
          {err && <p className="text-sm text-red-600" data-testid="reset-error">{err}</p>}
          <button type="submit" disabled={loading} className="btn-lime w-full py-3.5 disabled:opacity-60" data-testid="reset-submit-btn">
            {loading ? "Updating…" : "Update password"}
          </button>
        </form>
      )}
    </Shell>
  );
}
